import { useEffect, useState } from 'react'
import {
  api,
  type CapsuleOptions,
  type CatalogProduct,
  type ExcipientRequirementOption,
} from '../lib/api.ts'
import { traitLabels } from '../lib/capsuleShells.ts'
import { answerLabels } from '../lib/excipientAnswers.ts'
import { Button } from '../components/Button.tsx'
import { Checkbox } from '../components/Checkbox.tsx'
import { Tag } from '../components/Tag.tsx'
import { icons } from '../components/icons.ts'
import { Pagination } from '../components/Pagination.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { totalPages } from '../lib/pagination.ts'

/**
 * Twenty rows, and the server is asked for exactly that.
 *
 * The catalogue is the size of the pharmacy's own -- ArtPharma's runs to tens
 * of thousands -- so the page is what the API returns, not a slice of
 * something larger the browser already holds.
 */
const PAGE_SIZE = 20

/**
 * The pharmacy's catalogue, and what each product can be found by.
 *
 * Beside "Sinônimos" rather than instead of it, because they answer different
 * questions. That screen is a work queue — everything awaiting approval,
 * across the catalogue, in the order it arrived. This one starts from a
 * product: what is it called, what can it be found by, and is anything about
 * it waiting on somebody.
 *
 * Approving from here has context the queue cannot give you. A term is one of
 * the names a product answers to, and judging "Diip1" is a different act with
 * the product in front of you than in a list of orphaned strings.
 *
 * ## Why the pending filter is the first control
 *
 * A term awaiting approval is real work with a real cost: until somebody
 * approves it, retrieval cannot reach that product by that name, and the
 * resolver is left guessing at exactly the case an synonym would have settled
 * outright. The count is on the button so that cost is visible from here too,
 * not only from the queue.
 */
export function Products({
  onOpenProduct,
  onBack,
}: {
  onOpenProduct: (erpCode: string) => void
  onBack: () => void
}) {
  const [term, setTerm] = useState('')
  /** Only the excipients the account said something about (#617). */
  const [onlyExcipients, setOnlyExcipients] = useState(false)
  const [requirements, setRequirements] = useState<ExcipientRequirementOption[]>([])
  /** Only the capsule shells, by name or marked by the account (#636). */
  const [onlyShells, setOnlyShells] = useState(false)
  const [capsule, setCapsule] = useState<CapsuleOptions>({ axes: [], traits: [] })
  const [products, setProducts] = useState<CatalogProduct[] | undefined>()
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()

  /*
   * A changed search starts at the first page.
   *
   * Otherwise typing a term while on page 9 asks for rows 160-180 of a result
   * set with two pages, and the table comes back empty -- which reads as "no
   * such product" rather than "you are past the end".
   */
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    setOffset(0)
  }, [term, onlyExcipients, onlyShells])

  useEffect(() => {
    let live = true
    // Debounced: this queries the catalogue on every keystroke otherwise, and
    // a pharmacy's catalogue is tens of thousands of rows.
    const timer = setTimeout(() => {
      setLoading(true)
      setError(undefined)
      void api
        .catalog(term, false, PAGE_SIZE, offset, onlyExcipients, onlyShells)
        .then((r) => {
          if (!live) return
          setProducts(r.products)
          setTotal(r.total)
          setRequirements(r.excipientRequirements)
          setCapsule(r.capsule)
        })
        .finally(() => {
          if (live) setLoading(false)
        })
        .catch((cause: unknown) =>
          setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
        )
    }, 250)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [term, offset, attempt, onlyExcipients, onlyShells])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Produtos</h1>
      </div>

      <p className="text-tP text-text-weak max-w-prose">
        O catálogo da farmácia, como a busca o enxerga. Abra um produto para ver todos os termos que
        chegam nele e aprovar ou recusar os que estão esperando. Em <strong>Sinônimos</strong> está
        a fila de aprovação inteira; aqui você parte do produto.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-1 flex-col gap-1" style={{ minWidth: '16rem' }}>
          <span className="text-tTiny text-text-weak font-semibold uppercase">Nome ou código</span>
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="dipirona, 6187…"
            className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
          />
        </label>
        <Checkbox
          label="Só excipientes configurados"
          checked={onlyExcipients}
          onChange={(event) => setOnlyExcipients(event.target.checked)}
          containerClassName="pb-2"
        />
        <Checkbox
          label="Só invólucros"
          checked={onlyShells}
          onChange={(event) => setOnlyShells(event.target.checked)}
          containerClassName="pb-2"
        />
      </div>

      {products !== undefined && (
        <>
          <p className="text-tSmall text-text-weak">
            {/* The count stays put while a page loads, rather than flicking to
                zero and back: it is the same catalogue either way. */}
            {`${total === 0 ? 0 : offset + 1}–${offset + products.length} de ${total} produtos`}
          </p>

          <DataTable
            columns={productColumns(requirements, capsule)}
            rows={products}
            rowKey={(p) => p.erpCode}
            loading={loading}
            error={error}
            onRetry={() => setAttempt((n) => n + 1)}
            onRowClick={(p) => onOpenProduct(p.erpCode)}
            pageSize={PAGE_SIZE}
            empty={
              onlyExcipients && onlyShells
                ? 'Nenhum produto é ao mesmo tempo excipiente configurado e invólucro.'
                : onlyExcipients
                  ? 'Nenhum excipiente configurado. Abra um produto e marque para que tipo de ativo ele é indicado.'
                  : onlyShells
                    ? 'Nenhum invólucro: nenhum nome começa com CAP ou CAPSULA e nenhum produto foi marcado como invólucro.'
                    : total === 0
                      ? 'O catálogo ainda não foi sincronizado.'
                      : 'Nenhum produto com esse nome, código ou sinônimo.'
            }
          />

          <Pagination
            currentPage={Math.floor(offset / PAGE_SIZE) + 1}
            totalPages={totalPages(total, PAGE_SIZE)}
            onPageChange={(page: number) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  )
}

const productColumns = (
  requirements: readonly ExcipientRequirementOption[],
  capsule: CapsuleOptions,
): ReadonlyArray<Column<CatalogProduct>> => [
  { key: 'code', header: 'Código', className: 'font-mono', render: (p) => p.erpCode },
  {
    key: 'name',
    header: 'Produto',
    // What the product is the excipient for, and what it counts as when it is
    // a capsule shell, beside its name: the lists the Excipientes and Cápsulas
    // tabs used to give (#617, #636).
    render: (p) => (
      <span className="flex flex-wrap items-center gap-1">
        <span>{p.name}</span>
        {answerLabels(p.excipientAnswers ?? [], requirements).map((label) => (
          <Tag key={label} variant="stroke">
            {label}
          </Tag>
        ))}
        {p.shellTraits !== undefined && (
          <Tag variant="stroke">
            {p.shellTraits.length === 0
              ? 'invólucro'
              : `invólucro: ${traitLabels(p.shellTraits, capsule.traits).join(', ')}`}
          </Tag>
        )}
      </span>
    ),
  },
  {
    key: 'control',
    header: 'Controle',
    render: (p) =>
      p.controlledList === undefined ? (
        <span className="text-text-weak">—</span>
      ) : (
        <span className="text-text-warning font-semibold">{p.controlledList}</span>
      ),
  },
  /*
   * Zero is worth seeing. A product reachable by nothing but its own
   * catalogue name is the one a prescription written any other way will
   * miss entirely.
   */
  {
    key: 'terms',
    header: 'Termos',
    className: 'tabular-nums',
    render: (p) => (
      <span className={p.terms <= 1 ? 'text-text-warning' : 'text-text-weak'}>{p.terms}</span>
    ),
  },
  {
    key: 'active',
    header: 'Situação',
    render: (p) => (
      <span className={p.active ? 'text-text-weak' : 'text-text-warning'}>
        {p.active ? 'Ativo' : 'Inativo'}
      </span>
    ),
  },
]
